/**
 * The SPA's _app.tsx: Suspense + Loading, GTM realm guard, Better Auth guard,
 * the font wrapper (font-sans: Inter Variable from @nt/tokens), the
 * impersonation banner, the cookie banner (fixed at the bottom, early in the
 * DOM so the keyboard reaches it first), the platform-status banner (a
 * Cloudflare incident that breaks us, from the API Worker) on routes outside
 * the customer shell only, then the page. The page-level AppErrorBoundary sits
 * in the two layout routes (_shell, _bare) so it wraps the page the way
 * `getLayout(<AppErrorBoundary><Page/></AppErrorBoundary>)` did.
 */
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'

import Loading from '@/components/Loading'
import NotFoundPage from '@/components/not-found-page'
import GTMProvider from '@/components/providers/GTMProvider'
import { BetterAuthGuard } from '@/features/auth/ui/auth-guard'
import { ImpersonationBanner } from '@/features/auth/ui/impersonation-banner'
import { CookieConsentBanner } from '@/features/consent/ui/cookie-consent-banner'
import { PlatformStatusBanner } from '@/features/platform-status/ui/platform-status-banner'

/**
 * The router devtools panel, in development only.
 *
 * The flag is `process.env.NODE_ENV`, not `import.meta.env.DEV`, for one
 * mechanical reason: Jest compiles this file (test/browser/spa/root-shell.test.tsx
 * imports RootShell) with `module: commonjs`, where a bare `import.meta` is a
 * TypeScript error. `process.env.NODE_ENV` is substituted by vite.config.mts
 * `define` from the build MODE (vite/public-env.ts), which no env file can
 * flip, so it folds to a constant exactly the same way — Rollup drops the
 * dynamic import and the devtools never reach a production bundle. Under Jest
 * NODE_ENV is 'test', so the panel renders nothing and the shell test still
 * pins the same element order.
 */
const RouterDevtools =
  process.env.NODE_ENV === 'development'
    ? lazy(() =>
        import('@tanstack/react-router-devtools').then((m) => ({
          default: m.TanStackRouterDevtools,
        })),
      )
    : () => null

/**
 * The platform-status banner for every page outside the customer shell (`_bare`, the auth pages, the
 * 404). A `_shell` page gets it from `CustomerLayout`, in the content column beside the fixed sidebar,
 * so a page never shows two and the sidebar is never covered.
 */
function PlatformStatusBannerOutsideShell() {
  const inShell = useRouterState({ select: (state) => state.matches.some((match) => match.routeId === '/_shell') })
  return inShell ? null : <PlatformStatusBanner />
}

export function RootShell() {
  return (
    <Suspense fallback={<Loading />}>
      <GTMProvider>
        <BetterAuthGuard>
          <div className="font-sans">
            <ImpersonationBanner />
            <CookieConsentBanner />
            <PlatformStatusBannerOutsideShell />
            <Outlet />
          </div>
        </BetterAuthGuard>
      </GTMProvider>
      <RouterDevtools />
    </Suspense>
  )
}

export const Route = createRootRoute({
  component: RootShell,
  notFoundComponent: NotFoundPage,
})
